import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Spinner, Text, VStack, Heading, Icon, Flex, keyframes } from '@chakra-ui/react';
import { FiPlay, FiAlertCircle } from 'react-icons/fi';
import axiosInstance from '../services/axiosInstance';

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const PublicVideoPage = () => {
    // Hard-coded video IDs as specified by the user
    const VIDEO_ID = "69440ad01e1ad0140fcd86b4";
    const VDOCIPHER_ID = "bf4df6aec2614de5a398d391d0e012ec";

    const [otp, setOtp] = useState('');
    const [playbackInfo, setPlaybackInfo] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const playerRef = useRef(null);

    useEffect(() => {
        const fetchOtpAndPlaybackInfo = async () => {
            try {
                // Fetch OTP and playback info from the server
                const response = await axiosInstance.get(`/vdocipher/${VDOCIPHER_ID}`);
                const { otp, playbackInfo } = response.data;

                // Store them in state
                setOtp(otp);
                setPlaybackInfo(playbackInfo);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching OTP and playback info:', error);
                setError("Failed to load video. Please try again later.");
                setLoading(false);
            }
        };

        fetchOtpAndPlaybackInfo();
    }, []);

    if (loading) {
        return (
            <Box
                minHeight="100vh"
                bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                overflow="hidden"
            >
                {/* Animated background elements */}
                <Box
                    position="absolute"
                    top="-50%"
                    left="-50%"
                    width="200%"
                    height="200%"
                    bgGradient="radial(circle, rgba(255,255,255,0.1) 0%, transparent 70%)"
                    animation={`${pulse} 4s ease-in-out infinite`}
                />

                <VStack spacing={6} zIndex={1}>
                    <Box position="relative">
                        <Spinner
                            size="xl"
                            color="white"
                            thickness="4px"
                            speed="0.8s"
                        />
                        <Box
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                        >
                            <Icon as={FiPlay} w={6} h={6} color="white" />
                        </Box>
                    </Box>
                    <Text
                        fontSize="xl"
                        color="white"
                        fontWeight="600"
                        letterSpacing="wide"
                    >
                        Loading your video...
                    </Text>
                </VStack>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                minHeight="100vh"
                bgGradient="linear(135deg, #f093fb 0%, #f5576c 100%)"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <VStack
                    spacing={6}
                    bg="rgba(255, 255, 255, 0.95)"
                    p={10}
                    borderRadius="2xl"
                    boxShadow="2xl"
                    maxW="md"
                    backdropFilter="blur(10px)"
                >
                    <Flex
                        w={20}
                        h={20}
                        borderRadius="full"
                        bg="red.100"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon as={FiAlertCircle} w={10} h={10} color="red.500" />
                    </Flex>
                    <Heading size="lg" color="gray.800" textAlign="center">
                        Oops! Something went wrong
                    </Heading>
                    <Text color="gray.600" textAlign="center" fontSize="md">
                        {error}
                    </Text>
                </VStack>
            </Box>
        );
    }

    return (
        <Box
            minHeight="100vh"
            bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
            position="relative"
            overflow="hidden"
        >
            {/* Animated background pattern */}
            <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                opacity="0.1"
                backgroundImage="radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)"
            />

            <Container maxW="container.xl" py={{ base: 8, md: 16 }} position="relative" zIndex={1}>
                <VStack spacing={{ base: 6, md: 10 }} align="stretch">
                    {/* Logo and Header with gradient text */}
                    <VStack spacing={4}>
                        <Box
                            animation={`${fadeIn} 0.6s ease-out`}
                            p={4}
                            bg="rgba(255, 255, 255, 0.1)"
                            backdropFilter="blur(10px)"
                            borderRadius="2xl"
                            border="1px solid rgba(255, 255, 255, 0.2)"
                            boxShadow="0 4px 16px 0 rgba(31, 38, 135, 0.2)"
                        >
                            <img
                                src="/assets/logo.png"
                                alt="Cobra Logo"
                                style={{
                                    height: '80px',
                                    width: 'auto',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                        <Heading
                            as="h1"
                            size={{ base: "xl", md: "2xl" }}
                            textAlign="center"
                            bgGradient="linear(to-r, white, rgba(255,255,255,0.8))"
                            bgClip="text"
                            fontWeight="extrabold"
                            letterSpacing="tight"
                            animation={`${fadeIn} 0.8s ease-out`}
                        >
                            LES TAUX DE MARGE
                        </Heading>
                        <Box
                            height="4px"
                            width="100px"
                            bgGradient="linear(to-r, #ffd89b, #19547b)"
                            borderRadius="full"
                            animation={`${fadeIn} 1s ease-out`}
                        />
                    </VStack>

                    {/* Video Player Card with glassmorphism */}
                    <Box
                        animation={`${fadeIn} 1.2s ease-out`}
                        bg="rgba(255, 255, 255, 0.1)"
                        backdropFilter="blur(20px)"
                        border="1px solid rgba(255, 255, 255, 0.2)"
                        borderRadius="3xl"
                        overflow="hidden"
                        boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.37)"
                        transition="all 0.3s ease"
                        _hover={{
                            transform: "translateY(-4px)",
                            boxShadow: "0 12px 40px 0 rgba(31, 38, 135, 0.5)"
                        }}
                    >
                        <Box
                            position="relative"
                            paddingBottom="56.25%" // 16:9 aspect ratio
                            bg="black"
                        >
                            {otp && playbackInfo && (
                                <iframe
                                    ref={playerRef}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        border: 'none'
                                    }}
                                    src={`https://player.vdocipher.com/v2/?otp=${otp}&playbackInfo=${playbackInfo}`}
                                    allow="encrypted-media"
                                    allowFullScreen
                                    title="Video Player"
                                />
                            )}
                        </Box>
                    </Box>


                </VStack>
            </Container>

            {/* Floating particles effect */}
            <Box
                position="absolute"
                top="10%"
                left="5%"
                width="6px"
                height="6px"
                borderRadius="full"
                bg="white"
                opacity="0.4"
                animation={`${pulse} 3s ease-in-out infinite`}
            />
            <Box
                position="absolute"
                top="60%"
                right="10%"
                width="8px"
                height="8px"
                borderRadius="full"
                bg="white"
                opacity="0.3"
                animation={`${pulse} 4s ease-in-out infinite 1s`}
            />
            <Box
                position="absolute"
                bottom="20%"
                left="15%"
                width="4px"
                height="4px"
                borderRadius="full"
                bg="white"
                opacity="0.5"
                animation={`${pulse} 5s ease-in-out infinite 2s`}
            />
        </Box>
    );
};

export default PublicVideoPage;
